import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, FolderTree, ShoppingBag, BarChart3, Settings, LogOut,
  Link2, Palette, Sparkles, Truck, ChevronDown, Music, Users, Store, LayoutGrid,
} from 'lucide-react';
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar,
  SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { useApp } from '@/contexts/AppContext';
import { useTranslation } from 'react-i18next';

const productGroup = [
  { title: 'products', url: '/admin/products', icon: Package },
  { title: 'buyItWith', url: '/admin/buy-it-with', icon: Link2 },
  { title: 'productColors', url: '/admin/product-colors', icon: Palette },
  { title: 'newArrivals', url: '/admin/new-arrivals', icon: Sparkles },
  { title: 'sizeGuides', url: '/admin/size-guides', icon: Package },
];

const settingsGroup = [
  { title: 'general', url: '/admin/settings', icon: Settings },
  { title: 'appearance', url: '/admin/appearance', icon: Sparkles },
  { title: 'shipping', url: '/admin/shipping', icon: Truck },
  { title: 'sounds', url: '/admin/sounds', icon: Music },
];

const categoriesGroup = [
  { title: 'categories', url: '/admin/categories', icon: FolderTree },
  { title: 'subcategories', url: '/admin/subcategories', icon: FolderTree },
];

const single = [
  { title: 'overview', url: '/admin', icon: LayoutDashboard, end: true },
  { title: 'orders', url: '/admin/orders', icon: ShoppingBag },
  { title: 'users', url: '/admin/users', icon: Users },
  { title: 'analytics', url: '/admin/analytics', icon: BarChart3 },
];

// Map title keys to their translation path
const navKey = (title: string) => `admin.nav.${title}`;

export function AdminSidebar({ side = 'left' }: { side?: 'left' | 'right' }) {
  const { state, isMobile, setOpen, setOpenMobile } = useSidebar();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { signOut, user } = useApp();
  const handleSignOut = async () => { await signOut(); navigate('/'); };
  // On mobile, the sheet always shows full labels regardless of desktop collapsed state.
  const showLabels = isMobile || state !== 'collapsed';
  const desktopCollapsed = !isMobile && state === 'collapsed';

  const productActive = productGroup.some(i => pathname.startsWith(i.url));
  const settingsActive = settingsGroup.some(i => pathname.startsWith(i.url));
  const categoriesActive = categoriesGroup.some(i => pathname === i.url);

  const [productOpen, setProductOpen] = useState(productActive);
  const [settingsOpen, setSettingsOpen] = useState(settingsActive);
  const [categoriesOpen, setCategoriesOpen] = useState(categoriesActive);

  useEffect(() => { if (productActive) setProductOpen(true); }, [productActive]);
  useEffect(() => { if (settingsActive) setSettingsOpen(true); }, [settingsActive]);
  useEffect(() => { if (categoriesActive) setCategoriesOpen(true); }, [categoriesActive]);

  const linkCls = (active: boolean) =>
    `flex items-center gap-3 text-sidebar-foreground hover:text-sidebar-primary ${active ? 'text-sidebar-primary font-semibold' : ''}`;

  const handleParentClick = (
    e: React.MouseEvent,
    group: { url: string }[],
    setGroupOpen: (v: boolean) => void
  ) => {
    e.preventDefault();
    if (desktopCollapsed) {
      // Open sidebar, navigate to first item, expand its sublist
      setOpen(true);
      setGroupOpen(true);
      navigate(group[0].url);
    } else {
      // Toggle sublist
      setGroupOpen(!isOpenForGroup(group, setGroupOpen));
    }
  };

  // helper kept simple via closures below (avoid mismatch)
  function isOpenForGroup(group: { url: string }[], _setter: (v: boolean) => void) {
    if (group === productGroup) return productOpen;
    if (group === settingsGroup) return settingsOpen;
    if (group === categoriesGroup) return categoriesOpen;
    return false;
  }

  const renderGroup = (
    label: string,
    Icon: any,
    group: typeof productGroup,
    open: boolean,
    setGroupOpen: (v: boolean) => void,
    active: boolean,
  ) => (
    <Collapsible open={open && showLabels} onOpenChange={setGroupOpen} className="group/collapsible">
      <SidebarMenuItem>
        <SidebarMenuButton
          isActive={active}
          onClick={(e) => handleParentClick(e, group, setGroupOpen)}
          className={`${linkCls(active)} [&>span:last-child]:!whitespace-nowrap [&>span:last-child]:!overflow-visible [&>span:last-child]:!text-ellipsis-none`}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {showLabels && (
            <>
              <span className="whitespace-nowrap">{label}</span>
              <ChevronDown className="ms-auto h-3.5 w-3.5 shrink-0 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </>
          )}
        </SidebarMenuButton>
        <CollapsibleContent>
          <SidebarMenuSub>
            {group.map(item => {
              const itemActive = pathname === item.url || (item.url !== group[0].url && pathname.startsWith(item.url));
              return (
                <SidebarMenuSubItem key={item.url}>
                  <SidebarMenuSubButton asChild isActive={itemActive} className="[&>span:last-child]:!whitespace-nowrap [&>span:last-child]:!overflow-visible">
                    <NavLink
                      to={item.url}
                      onClick={() => isMobile && setOpenMobile(false)}
                      className={linkCls(itemActive)}
                    >
                      <item.icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="whitespace-nowrap">{t(navKey(item.title))}</span>
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );

  return (
    <Sidebar collapsible="icon" side={side} className="border-sidebar-border">
      <SidebarHeader className="p-4 border-b border-sidebar-border">
        <div className="font-display text-xl font-bold text-sidebar-primary truncate max-w-full">
          {showLabels ? (user?.fullName || (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '') || user?.email || 'SEEN') : 'S'}
        </div>
      </SidebarHeader>
      <SidebarContent className="text-sidebar-foreground">
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/60">{t('admin.dashboard')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {single.slice(0, 1).map(item => {
                const active = pathname === item.url;
                return (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild isActive={active} className="[&>span:last-child]:!whitespace-nowrap [&>span:last-child]:!overflow-visible">
                      <NavLink to={item.url} end onClick={() => isMobile && setOpenMobile(false)} className={linkCls(active)}>
                        <item.icon className="h-4 w-4 shrink-0" />
                        {showLabels && <span className="whitespace-nowrap">{t(navKey(item.title))}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}

              {renderGroup(t('admin.nav.products'), Package, productGroup, productOpen, setProductOpen, productActive)}
              {renderGroup(t('admin.nav.categories'), FolderTree, categoriesGroup, categoriesOpen, setCategoriesOpen, categoriesActive)}

              {single.slice(1).map(item => {
                const active = pathname === item.url || pathname.startsWith(item.url + '/');
                return (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild isActive={active} className="[&>span:last-child]:!whitespace-nowrap [&>span:last-child]:!overflow-visible">
                      <NavLink to={item.url} onClick={() => isMobile && setOpenMobile(false)} className={linkCls(active)}>
                        <item.icon className="h-4 w-4 shrink-0" />
                        {showLabels && <span className="whitespace-nowrap">{t(navKey(item.title))}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}

              {renderGroup(t('admin.nav.general'), Settings, settingsGroup, settingsOpen, setSettingsOpen, settingsActive)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-border/50 py-3 mt-auto">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <NavLink
                to="/"
                className="flex items-center gap-3 text-sidebar-foreground hover:text-sidebar-primary w-full"
              >
                <Store className="h-4 w-4 shrink-0" />
                {showLabels && <span>{t('admin.nav.storefront', 'Storefront')}</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleSignOut} className="text-destructive hover:text-destructive">
              <LogOut className="h-4 w-4 shrink-0" />
              {showLabels && <span>{t('auth.signOut')}</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
